import { useCallback } from "react";
import { useNavigate, NavigateOptions } from "react-router-dom";
import { useLocale } from "./use-locale";

const isStandalone = import.meta.env.VITE_STANDALONE !== "false";

/**
 * Returns a navigate function that accepts locale-prefixed paths (e.g. `/ar/quote-list`)
 * and works correctly in both standalone and MF modes.
 *
 * - Standalone: passes the path straight through (router root is `/`).
 * - MF: strips the `/<lng>` prefix so the path is relative to the basename
 *   (`/<lng>/travel`), avoiding the double-prefix `/ar/travel/ar/...` bug.
 */
export const useNavigateLocale = () => {
  const navigate = useNavigate();
  const locale = useLocale();

  return useCallback(
    (path: string, options?: NavigateOptions) => {
      if (isStandalone) {
        navigate(path, options);
        return;
      }
      // Strip leading /<lng> so the router receives a path relative to its basename.
      const stripped = path.replace(new RegExp(`^/${locale}`), "") || "/";
      navigate(stripped, options);
    },
    [navigate, locale],
  );
};
